import React, { useEffect, useMemo, useState } from 'react';
import SEOHelmet from '../components/SEOHelmet';

const teamMembers = [
  {
    id: 1,
    name: 'Mohamed Bejaoui',
    role: 'Chair',
    initials: 'M',
    image: '/ORGINAIZER/mohamed bejaoui.jpg',
  },
  {
    id: 2,
    name: 'Amal Benjamaa',
    role: 'Treasurer',
    initials: 'A',
    image: '/ORGINAIZER/Amal Benjamaa.jpg',
  },
  {
    id: 3,
    name: 'Yosra Hajji',
    role: 'Logistics Manager',
    initials: 'Y',
    image: '/ORGINAIZER/Yosra Hajji.jpg',
  }, 
  {
    id: 4,
    name: 'Refka Mimouni',
    role: 'Media Manager',
    initials: 'R',
    image: '/ORGINAIZER/Refka Mimouni.jpeg',
  },
  {
    id: 5,
    name: 'Karim Chatti',
    role: 'Speakers Manager',
    initials: 'K',
    image: '/ORGINAIZER/karim chatti.jpg',
  },
  {
    id: 6,
    name: 'Ibrahim Elmaazi',
    role: 'Ambassador Coordinator',
    initials: 'I',
    image: '/ORGINAIZER/Ibrahim Elmaazi.jpg',
  },
  {
    id: 7,
    name: 'Firas Cherif',
    role: 'Sponsoring Manager',
    initials: 'F',
    image: '/ORGINAIZER/firas cherif.jpg',
  },
];

function Team() {
  const getCardsPerView = () => {
    if (typeof window === 'undefined') return 4;
    if (window.innerWidth <= 520) return 1;
    if (window.innerWidth <= 900) return 2;
    return 4;
  };

  const [cardsPerView, setCardsPerView] = useState(getCardsPerView);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isSliding, setIsSliding] = useState(false);

  const goToIndex = (nextIndex) => {
    setIsSliding(true);
    setCurrentIndex(nextIndex);
  };

  const handlePointerEnter = (event) => {
    if (event.pointerType === 'mouse') {
      setIsPaused(true);
    }
  };

  const handlePointerLeave = (event) => {
    if (event.pointerType === 'mouse') {
      setIsPaused(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = useMemo(
    () => Math.max(teamMembers.length - cardsPerView, 0),
    [cardsPerView]
  );

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [cardsPerView, maxIndex]);

  useEffect(() => {
    if (isPaused || teamMembers.length <= cardsPerView) return undefined;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = prev >= maxIndex ? 0 : prev + 1;
        setIsSliding(true);
        return next;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused, cardsPerView, maxIndex]);

  useEffect(() => {
    if (!isSliding) return undefined;
    const timeout = setTimeout(() => setIsSliding(false), 280);
    return () => clearTimeout(timeout);
  }, [isSliding]);

  return (
    <div>
      <SEOHelmet 
        title="Our Team - Code It Up 6.0 Organizers"
        description="Meet the amazing team of organizers and coordinators behind Code It Up 6.0 hackathon. Learn about the people making this event possible."
        keywords="code it up team, organizers, hackathon organizers, event team, coordinators"
        canonical="https://code-it-up-6.netlify.app/team"
      />
      <h1 style={{ textAlign: 'center' }}>OUR TEAM</h1>
      <p className="section-subtitle">
        Meet the amazing organizers behind Code It Up 6.0!
      </p>

      <div
        className="team-slider"
        style={{ '--cards-per-view': cardsPerView }}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={() => setIsPaused(false)}
      >
        <div
          className={`team-track ${isSliding ? 'is-sliding' : ''}`}
          style={{ transform: `translateX(-${(currentIndex * 100) / cardsPerView}%)` }}
        >
          {teamMembers.map((member) => (
            <div key={member.id} className="team-slide-item">
              <article className="team-member">
                <div className="team-member-avatar">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="team-member-avatar-image"
                      loading="lazy"
                    />
                  ) : (
                    <span>{member.initials}</span>
                  )}
                </div>
                <div className="team-member-info">
                  <h3>{member.name}</h3>
                  <p className="team-member-role">{member.role}</p>
                </div>
                <div className="team-member-wave"></div>
              </article>
            </div>
          ))}
        </div>

        {maxIndex > 0 && (
          <div className="team-slider-dots" aria-label="Team slider pagination">
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={`team-slider-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToIndex(index)}
                aria-label={`Go to team slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Team;
